/**
 * Shared display helpers for the paid-order fulfillment and payment axes (PBI 1011).
 * Centralizes badge colors and business-rule predicates used across admin and
 * customer-facing order views.
 */

import { FulfillmentStatus, PaymentStatus } from '../types';

/** Mantine badge colors keyed by fulfillment status. */
export const FULFILLMENT_STATUS_COLORS: Record<FulfillmentStatus, string> = {
  [FulfillmentStatus.Pending]: 'blue',
  [FulfillmentStatus.Placed]: 'green',
  [FulfillmentStatus.Shipped]: 'teal',
  [FulfillmentStatus.Delivered]: 'green',
  [FulfillmentStatus.Failed]: 'red',
  [FulfillmentStatus.Cancelled]: 'gray',
  [FulfillmentStatus.Undeliverable]: 'orange',
};

/** Mantine badge colors keyed by payment status. */
export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  [PaymentStatus.None]: 'gray',
  [PaymentStatus.Authorized]: 'yellow',
  [PaymentStatus.Captured]: 'green',
  [PaymentStatus.CaptureFailed]: 'orange',
  [PaymentStatus.Voided]: 'gray',
  [PaymentStatus.RefundPending]: 'grape',
  [PaymentStatus.Refunded]: 'teal',
  [PaymentStatus.PartiallyRefunded]: 'cyan',
  [PaymentStatus.AuthorizationFailed]: 'red',
};

/** Splits PascalCase enum strings into spaced labels for display. */
export const formatStatusLabel = (status: string): string =>
  status.replace(/([A-Z])/g, ' $1').trim();

/**
 * Formats the admin operations combined status (`Fulfillment/Payment`) for display.
 */
export const formatCombinedOrderStatusLabel = (combinedStatus: string): string => {
  const slash = combinedStatus.indexOf('/');
  if (slash === -1) {
    return formatStatusLabel(combinedStatus);
  }

  const fulfillment = combinedStatus.slice(0, slash);
  const payment = combinedStatus.slice(slash + 1);
  return `${formatStatusLabel(fulfillment)} / ${formatStatusLabel(payment)}`;
};

/**
 * Resolves a badge color for admin operations rows. E-card statuses pass through
 * unchanged; flower rows use the combined `Fulfillment/Payment` string.
 */
export const getOperationEventStatusColor = (
  status: string,
  eCardStatusColors: Record<string, string>,
): string => {
  const slash = status.indexOf('/');
  if (slash === -1) {
    return eCardStatusColors[status] ?? 'gray';
  }

  const fulfillment = status.slice(0, slash) as FulfillmentStatus;
  const payment = status.slice(slash + 1) as PaymentStatus;

  if (payment === PaymentStatus.CaptureFailed
    || payment === PaymentStatus.Authorized
    || payment === PaymentStatus.AuthorizationFailed) {
    return PAYMENT_STATUS_COLORS[payment];
  }

  return FULFILLMENT_STATUS_COLORS[fulfillment] ?? PAYMENT_STATUS_COLORS[payment] ?? 'gray';
};

/** True when the user cannot cancel in-app (vendor placed or payment in-flight). */
export const isPaidOrderCancelBlocked = (
  fulfillmentStatus: FulfillmentStatus,
  paymentStatus: PaymentStatus,
): boolean =>
  fulfillmentStatus === FulfillmentStatus.Placed
  || paymentStatus === PaymentStatus.Authorized
  || paymentStatus === PaymentStatus.CaptureFailed;

/** True when a pending or needs-review pre-order can be cancelled (mirrors PaidOrderStatusQueries.IsCancellable). */
export const canCancelPaidOrder = (
  fulfillmentStatus: FulfillmentStatus,
  paymentStatus: PaymentStatus,
): boolean =>
  (fulfillmentStatus === FulfillmentStatus.Pending
    && (paymentStatus === PaymentStatus.None
      || paymentStatus === PaymentStatus.Authorized
      || paymentStatus === PaymentStatus.AuthorizationFailed))
  || (fulfillmentStatus === FulfillmentStatus.Failed
    && paymentStatus === PaymentStatus.Voided);

/** True when a flower pre-order can be edited (Pending draft or Failed needs-review). */
export const canEditPaidOrder = (
  fulfillmentStatus: FulfillmentStatus,
  paymentStatus: PaymentStatus,
): boolean =>
  (fulfillmentStatus === FulfillmentStatus.Pending && paymentStatus === PaymentStatus.None)
  || (fulfillmentStatus === FulfillmentStatus.Failed && paymentStatus === PaymentStatus.Voided);

/** True when admin Place Order Now is allowed for a flower pre-order. */
export const canAdminPlaceFlowerOrder = (
  fulfillmentStatus: FulfillmentStatus,
  paymentStatus: PaymentStatus,
): boolean =>
  fulfillmentStatus === FulfillmentStatus.Pending
  && (paymentStatus === PaymentStatus.None
    || paymentStatus === PaymentStatus.Authorized
    || paymentStatus === PaymentStatus.AuthorizationFailed);

/**
 * True when admin Retry Place is allowed for a mailed-card order (PBI #1157).
 * Caller must also ensure CardlyOrderId is null (double-place guard).
 */
export const canAdminRetryPlaceMailedCardOrder = (
  fulfillmentStatus: FulfillmentStatus,
  paymentStatus: PaymentStatus,
): boolean =>
  fulfillmentStatus === FulfillmentStatus.Failed
  && paymentStatus === PaymentStatus.Voided;

/**
 * Placement retry fields shared by admin order DTOs (PBI #1220).
 * Mirrors the server-side `IPlacementRetryStatusView`.
 */
export interface PlacementRetryStatus {
  isRetryingPlacement: boolean;
  placementAttemptCount: number;
  lastPlacementError?: string | null;
  placementEligibleAtUtc?: string | null;
}

/**
 * True when a failed placement is still being retried automatically (PBI #1220).
 *
 * Business rule: retrying is not its own status. The server derives it from the status axes
 * plus retry bookkeeping and sends `isRetryingPlacement`, so every surface reads that single
 * flag rather than re-deriving the condition and drifting from the audit trail.
 */
export const isRetryingPlacement = (order: Partial<PlacementRetryStatus> | null | undefined): boolean =>
  order?.isRetryingPlacement === true;

/**
 * Short badge label for an order that is still being retried, e.g. `Retrying (attempt 3)`.
 * Returns null when the order is not retrying, so callers can render nothing.
 */
export const formatRetryingBadgeLabel = (
  order: Partial<PlacementRetryStatus> | null | undefined,
): string | null => {
  if (!isRetryingPlacement(order)) {
    return null;
  }

  const attempt = order?.placementAttemptCount ?? 0;

  return attempt > 0 ? `Retrying (attempt ${attempt})` : 'Retrying';
};

/** Mantine badge color for the retrying state — in flight, not yet a failure. */
export const RETRYING_PLACEMENT_BADGE_COLOR = 'yellow';

/** Parses the operations timeline combined status into its two axes. */
export const parseCombinedOrderStatus = (
  combinedStatus: string,
): { fulfillmentStatus: FulfillmentStatus; paymentStatus: PaymentStatus } | null => {
  const slash = combinedStatus.indexOf('/');
  if (slash === -1) {
    return null;
  }

  return {
    fulfillmentStatus: combinedStatus.slice(0, slash) as FulfillmentStatus,
    paymentStatus: combinedStatus.slice(slash + 1) as PaymentStatus,
  };
};
